import type { CollectionModelFieldNumber } from '../fields/number.field';
import type { CollectionModelFieldOrientation } from '../fields/orientation.field';

export type NumberToOrientationResult =
	| { status: 'ok' }
	| { status: 'normalize'; sourceValue: string }
	| { status: 'dropped-sag'; sourceDisplay: string };

const MIN_BEARING = 0;
const MAX_BEARING = 360;

/**
 * Copy a Number field's state into an Orientation field during a form upgrade.
 *
 * - Manual values inside `[0, 360]` transfer directly.
 * - Manual values outside that range are normalized by the Orientation mutator; this
 *   function reports `'normalize'` so the upgrade can preview the change to the user.
 * - Bearing / back-bearing transfer by capture id
 * - Sag is not available Orientation
 */
export function convertNumberToOrientation(
	oldField: CollectionModelFieldNumber,
	newField: CollectionModelFieldOrientation
): NumberToOrientationResult {
	if (oldField.isManual()) {
		const value = oldField.stateValue.value;
		newField.Mutator.manual(value);
		if (value >= MIN_BEARING && value <= MAX_BEARING) {
			return { status: 'ok' };
		}
		return { status: 'normalize', sourceValue: `${value} → ${newField.value}` };
	}

	if (oldField.isBearing()) {
		newField.Mutator.bearing(oldField.stateValue.captureId);
		return { status: 'ok' };
	}

	if (oldField.isBackBearing()) {
		newField.Mutator.backBearing(oldField.stateValue.captureId);
		return { status: 'ok' };
	}

	if (oldField.isSag()) {
		newField.Mutator.manual(null);
		return { status: 'dropped-sag', sourceDisplay: oldField.displayValue };
	}

	return { status: 'ok' };
}
